// Story 4.2 — pure helpers for vault-grid rendering decisions.
//
// Story 3.4's <SealedDossier showRetirementWarning> prop is
// consumer-driven (the component module-header L94-L97 explicitly
// documents NON-derivation from dossier.retiring_at). Story 4.2 is the
// first consumer that needs the derivation; this file owns it.
//
// Derivation rule per Story 4.2 AC7:
//   showRetirementWarning =
//     (drop_status === "live" AND retiring_at !== null
//        AND retiring_at - now <= 14 days)
//     OR drop_status === "retiring"
//
// drop_status === "retired" → NOT a warning, render state="retired"
// (the foil overlay) instead.
//
// Pure function; no React deps. Unit-tested with boundary cases.

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export interface RetirementWarningInput {
  drop_status: "scheduled" | "live" | "retiring" | "retired" | string;
  retiring_at: string | null;
}

/// Returns true when the SealedDossier component should render the
/// "2 weeks left" warning badge. The "now" parameter is injectable so
/// tests can drive determinism without mocking Date.
export function deriveShouldShowRetirementWarning(
  item: RetirementWarningInput,
  now: Date = new Date(),
): boolean {
  if (item.drop_status === "retiring") {
    return true;
  }
  if (item.drop_status !== "live" || item.retiring_at === null) {
    return false;
  }
  const retiringAt = new Date(item.retiring_at);
  const remainingMs = retiringAt.getTime() - now.getTime();
  // Past retiring_at (negative remaining) → already retiring; show
  // warning. Within 14 days → show warning. Beyond 14 days → don't.
  return remainingMs <= TWO_WEEKS_MS;
}
